import "./EditorPropiedades.css";

import { useEffect, useState } from "react";
import { FaChevronLeft, FaChevronRight, FaTimes } from "react-icons/fa";
import { isAllowedImageSource } from "../imageSource";

const clampNumber = (value, min, max) => {
    const number = Number(value);
    return Number.isFinite(number) ? Math.min(max, Math.max(min, number)) : min;
};

const palette = ["#1F2937", "#1D4ED8", "#0F766E", "#B45309", "#B91C1C", "#FFFFFF"];


function EditorPropiedades({
    element,
    onUpdate,
    collapsed = false,
    onToggleCollapse,
    panelOpen = false,
    onClosePanel,
    dynamicFields = null,
}) {

    const [imageUrl, setImageUrl] = useState(element?.imageSrc || "");
    const [imageError, setImageError] = useState("");

    useEffect(() => {
        setImageUrl(element?.imageSrc || "");
        setImageError("");
    }, [element?.id, element?.imageSrc]);

    const applyImageUrl = () => {
        const source = imageUrl.trim();
        if (!source) {
            update({ imageSrc: "", imageLoadError: false });
            setImageError("");
            return;
        }
        if (!isAllowedImageSource(source)) {
            setImageError("Usa una URL http o https válida para una imagen.");
            return;
        }
        update({ imageSrc: source, imageLoadError: false });
        setImageError("");
    };

    const uploadImage = event => {
        const file = event.target.files?.[0];
        if (!file) return;

        const allowedTypes = ["image/png", "image/jpeg", "image/webp", "image/gif", "image/avif"];
        if (!allowedTypes.includes(file.type)) {
            setImageError("Elige una imagen PNG, JPG, WEBP, GIF o AVIF.");
            event.target.value = "";
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            setImageError("La imagen no puede superar los 5 MB.");
            event.target.value = "";
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            if (typeof reader.result !== "string" || !isAllowedImageSource(reader.result)) {
                setImageError("No se pudo leer el archivo de imagen.");
                return;
            }
            update({ imageSrc: reader.result, imageLoadError: false });
            setImageUrl("");
            setImageError("");
        };
        reader.onerror = () => setImageError("No se pudo leer el archivo de imagen.");
        reader.readAsDataURL(file);
        event.target.value = "";
    };


    const update = changes => onUpdate(element.id, changes);

    const panelClassName = `editor-properties${collapsed ? " editor-properties-collapsed" : ""}${panelOpen ? " editor-properties-panel-open" : ""}`;
    const panelAttributes = panelOpen
        ? { role: "dialog", "aria-modal": "true", "aria-labelledby": "editor-properties-title" }
        : {};
    const collapseButton = (
        <button
            type="button"
            className="properties-collapse-button"
            aria-label={collapsed ? "Expandir propiedades" : "Contraer propiedades"}
            aria-expanded={!collapsed}
            title={collapsed ? "Expandir propiedades" : "Contraer propiedades"}
            onClick={onToggleCollapse}
        >
            {collapsed ? <FaChevronLeft aria-hidden="true" /> : <FaChevronRight aria-hidden="true" />}
        </button>
    );
    const closeButton = (
        <button
            type="button"
            className="properties-mobile-close"
            aria-label="Cerrar propiedades"
            onClick={onClosePanel}
        >
            <FaTimes aria-hidden="true" />
        </button>
    );

    if (collapsed) {
        return (
            <aside className={panelClassName} aria-label="Propiedades" {...panelAttributes}>
                <div className="properties-header">{collapseButton}</div>
            </aside>
        );
    }

    if (!element) {
        return (
            <aside className={panelClassName} aria-label="Propiedades" {...panelAttributes}>
                <div className="properties-header">
                    <h3 id="editor-properties-title">Propiedades</h3>
                    {collapseButton}
                    {closeButton}
                </div>
                <div className="properties-body">
                    <div className="empty-properties">
                        <div>
                            <strong>Ningún elemento seleccionado</strong>
                            <span>Selecciona un elemento del certificado para editar sus propiedades.</span>
                        </div>
                    </div>
                </div>
            </aside>
        );
    }

    return (
        <aside className={panelClassName} aria-label="Propiedades" {...panelAttributes} onMouseDown={event => event.stopPropagation()} onClick={event => event.stopPropagation()}>
            <div className="properties-header">
                <h3 id="editor-properties-title">Propiedades</h3>
                {collapseButton}
                {closeButton}
            </div>
            <div className="properties-body">
                {/* TEXTO */}

                {element.type === "text" && (

                    <div className="property-group">

                        <label>
                            Texto
                        </label>

                        <textarea

                            value={
                                element.text
                            }

                            onChange={e =>
                                update({
                                    text:
                                    e.target.value
                                })
                            }

                        />

                    </div>

                )}


                {/* CAMPO DINÁMICO */}

                {element.type === "dynamic" && (

                    <div className="property-group">

                        <label htmlFor="dynamic-variable">
                            Variable
                        </label>

                        {dynamicFields === null ? (
                            <input
                                id="dynamic-variable"
                                type="text"
                                value={element.variable}
                                placeholder="ciudadano.nombre"
                                onChange={event => update({ variable: event.target.value })}
                            />
                        ) : (
                            <select
                                id="dynamic-variable"
                                aria-label="Variable"
                                value={element.variable || ""}
                                onChange={event => update({ variable: event.target.value })}
                            >
                                <option value="">Selecciona un campo</option>
                                {element.variable && !dynamicFields.includes(element.variable) && (
                                    <option value={element.variable}>
                                        {element.variable} (no disponible en esta versión)
                                    </option>
                                )}
                                {dynamicFields.map(field => (
                                    <option key={field} value={field}>{field}</option>
                                ))}
                            </select>
                        )}

                        <small>
                            {dynamicFields === null
                                ? <>Ejemplo: ciudadano.nombre</>
                                : dynamicFields.length
                                    ? "Los campos corresponden a la versión seleccionada del XSD."
                                    : "Esta versión del XSD no tiene campos disponibles."}
                        </small>

                    </div>

                )}


                {element.type === "checkbox" && (
                    <>
                        <div className="property-group">
                            <label htmlFor="checkbox-label">Etiqueta</label>
                            <textarea
                                id="checkbox-label"
                                value={element.text || ""}
                                onChange={event => update({ text: event.target.value })}
                            />
                        </div>
                        <div className="property-group">
                            <label htmlFor="checkbox-color">Color</label>
                            <input
                                id="checkbox-color"
                                type="color"
                                value={element.color || "#1F2937"}
                                onChange={event => update({ color: event.target.value })}
                            />
                        </div>
                        <label className="checkbox-property-control" htmlFor="checkbox-checked">
                            <input
                                id="checkbox-checked"
                                type="checkbox"
                                checked={Boolean(element.checked)}
                                onChange={event => update({ checked: event.target.checked })}
                            />
                            Marcar como verificada
                        </label>
                    </>
                )}


                {element.type === "image" && (
                    <div className="property-section image-properties">
                        <h4>Imagen</h4>
                        <div className="property-group">
                            <label htmlFor="image-url">URL de imagen</label>
                            <div className="image-url-row">
                                <input
                                    id="image-url"
                                    type="url"
                                    value={imageUrl}
                                    placeholder="https://ejemplo.com/logo.png"
                                    onChange={event => setImageUrl(event.target.value)}
                                    onKeyDown={event => {
                                        if (event.key === "Enter") applyImageUrl();
                                    }}
                                />
                                <button type="button" onClick={applyImageUrl}>Usar URL</button>
                            </div>
                        </div>
                        <div className="property-group">
                            <label htmlFor="image-upload">Subir desde el dispositivo</label>
                            <input
                                id="image-upload"
                                type="file"
                                accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
                                onChange={uploadImage}
                            />
                            <small>PNG, JPG, WEBP, GIF o AVIF · máximo 5 MB</small>
                        </div>
                        {imageError && <p className="image-error" role="alert">{imageError}</p>}
                    </div>
                )}


                {/* ANCHO */}

                <div className="property-group">

                    <label htmlFor="element-width">
                        Ancho
                    </label>

                    <input type="number"
                        id="element-width"
                        min="1"
                        max={Math.max(1, 700 - element.x)}
                        step="1"

                        value={
                            Math.round(
                                element.width
                            )
                        }

                        onChange={e =>
                            update({
                                width:
                                    clampNumber(e.target.value, 1, Math.max(1, 700 - element.x))
                            })
                        }

                    />

                </div>


                {/* ALTO */}

                <div className="property-group">

                        <label htmlFor="element-height">
                        Alto
                    </label>

                    <input

                        type="number"
                            id="element-height"
                        min="1"
                        max={Math.max(1, 990 - element.y)}
                        step="1"

                        value={
                            Math.round(
                                element.height
                            )
                        }

                        onChange={e =>
                            update({
                                height:
                                    clampNumber(e.target.value, 1, Math.max(1, 990 - element.y))
                            })
                        }

                    />

                </div>


                {/* POSICIÓN */}

                <div className="property-row">


                    <div className="property-group">

                        <label htmlFor="element-x">
                            X
                        </label>

                        <input

                            type="number"
                            id="element-x"
                            min="0"
                            max={Math.max(0, 700 - element.width)}
                            step="1"

                            value={
                                Math.round(
                                    element.x
                                )
                            }

                            onChange={e =>
                                update({
                                    x:
                                        clampNumber(e.target.value, 0, Math.max(0, 700 - element.width))
                                })
                            }

                        />

                    </div>


                    <div className="property-group">

                        <label htmlFor="element-y">
                            Y
                        </label>

                        <input

                            type="number"
                            id="element-y"
                            min="0"
                            max={Math.max(0, 990 - element.height)}
                            step="1"

                            value={
                                Math.round(
                                    element.y
                                )
                            }

                            onChange={e =>
                                update({
                                    y:
                                        clampNumber(e.target.value, 0, Math.max(0, 990 - element.height))
                                })
                            }

                        />

                    </div>


                </div>


                {/* TAMAÑO DE FUENTE */}

                {(element.type === "text" ||
                    element.type === "dynamic") && (

                    <div className="property-group">

                        <label htmlFor="element-font-size">
                            Tamaño de texto
                        </label>

                        <input

                            type="number"
                            id="element-font-size"
                            min="1"
                            max="96"
                            step="1"

                            value={
                                element.fontSize
                            }

                            onChange={e =>
                                update({
                                    fontSize:
                                        clampNumber(e.target.value, 1, 96)
                                })
                            }

                        />

                    </div>

                )}


                {(element.type === "text" || element.type === "dynamic") && (
                    <>
                        <div className="property-group">
                            <label htmlFor="text-color">Color de texto</label>
                            <div className="color-control-row">
                                <input
                                    id="text-color"
                                    type="color"
                                    value={element.color || "#1F2937"}
                                    onChange={event => update({ color: event.target.value })}
                                />
                                <span>{(element.color || "#1F2937").toUpperCase()}</span>
                            </div>
                            <div className="color-palette">
                                {palette.map(color => (
                                    <button
                                        key={color}
                                        type="button"
                                        className="color-swatch"
                                        style={{ backgroundColor: color }}
                                        aria-label={`Color de texto ${color}`}
                                        onClick={() => update({ color })}
                                    />
                                ))}
                            </div>
                        </div>

                        <div className="property-group">
                            <label htmlFor="text-background">Fondo de texto</label>
                            <div className="color-control-row">
                                <input
                                    id="text-background"
                                    type="color"
                                    value={element.background && element.background !== "transparent" ? element.background : "#FFFFFF"}
                                    onChange={event => update({ background: event.target.value })}
                                />
                                <button type="button" className="clear-background-btn" onClick={() => update({ background: "transparent" })}>
                                    Sin fondo
                                </button>
                            </div>
                        </div>

                        <div className="property-row">
                            <div className="property-group">
                                <label htmlFor="text-weight">Peso</label>
                                <select id="text-weight" value={element.fontWeight || "normal"} onChange={event => update({ fontWeight: event.target.value })}>
                                    <option value="normal">Normal</option>
                                    <option value="bold">Negrita</option>
                                </select>
                            </div>
                            <div className="property-group">
                                <label htmlFor="text-style">Estilo</label>
                                <select id="text-style" value={element.fontStyle || "normal"} onChange={event => update({ fontStyle: event.target.value })}>
                                    <option value="normal">Normal</option>
                                    <option value="italic">Cursiva</option>
                                </select>
                            </div>
                        </div>

                        <div className="property-group">
                            <label htmlFor="text-font-family">Tipografía</label>
                            <select
                                id="text-font-family"
                                value={element.fontFamily || "Arial, sans-serif"}
                                onChange={event => update({ fontFamily: event.target.value })}
                            >
                                <option value="Arial, sans-serif">Sans serif</option>
                                <option value="Georgia, serif">Georgia</option>
                                <option value="Times New Roman, serif">Times New Roman</option>
                                <option value="Verdana, sans-serif">Verdana</option>
                            </select>
                        </div>
                    </>
                )}


                {(element.type === "rectangle" || element.type === "ellipse") && (
                    <>
                        <div className="property-group">
                            <label htmlFor="shape-fill">Relleno</label>
                            <div className="color-control-row">
                                <input
                                    id="shape-fill"
                                    type="color"
                                    value={element.fillColor && element.fillColor !== "transparent" ? element.fillColor : "#DCE8F5"}
                                    onChange={event => update({ fillColor: event.target.value })}
                                />
                                <button type="button" className="clear-background-btn" onClick={() => update({ fillColor: "transparent" })}>
                                    Sin relleno
                                </button>
                            </div>
                        </div>
                        <div className="property-row shape-border-row" role="group" aria-label="Propiedades de borde">
                            <div className="property-group shape-border-toggle">
                                <label className="checkbox-property-control" htmlFor="shape-border-enabled">
                                    <input
                                        id="shape-border-enabled"
                                        type="checkbox"
                                        checked={element.borderEnabled !== false}
                                        onChange={event => update({ borderEnabled: event.target.checked })}
                                    />
                                    Mostrar borde
                                </label>
                            </div>
                            <div className="property-group">
                                <label htmlFor="shape-border-color">Borde</label>
                                <input
                                    id="shape-border-color"
                                    type="color"
                                    value={element.borderColor || "#315B7D"}
                                    disabled={element.borderEnabled === false}
                                    onChange={event => update({ borderColor: event.target.value })}
                                />
                            </div>
                            <div className="property-group">
                                <label htmlFor="shape-border-width">Grosor</label>
                                <input
                                    id="shape-border-width"
                                    type="number"
                                    min="0"
                                    max="20"
                                    value={element.borderWidth ?? 2}
                                    disabled={element.borderEnabled === false}
                                    onChange={event => update({ borderWidth: clampNumber(event.target.value, 0, 20) })}
                                />
                            </div>
                        </div>
                    </>
                )}


                {element.type === "line" && (
                    <div className="property-group">
                        <label htmlFor="line-color">Color de línea</label>
                        <input
                            id="line-color"
                            type="color"
                            value={element.color || "#1F2937"}
                            onChange={event => update({ color: event.target.value })}
                        />
                    </div>
                )}


                {/* ALINEACIÓN */}

                {(element.type === "text" ||
                    element.type === "dynamic") && (

                    <div className="property-group">

                        <label>
                            Alineación
                        </label>

                        <select

                            value={
                                element.textAlign
                            }

                            onChange={e =>
                                update({
                                    textAlign:
                                    e.target.value
                                })
                            }

                        >

                            <option value="left">
                                Izquierda
                            </option>

                            <option value="center">
                                Centrado
                            </option>

                            <option value="right">
                                Derecha
                            </option>

                        </select>

                    </div>

                )}


            </div>


        </aside>

    );

}


export default EditorPropiedades;
